import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Button,
  Dialog,
  TextField,
  Divider,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import dayjs from "dayjs";
import { styled } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import { useTheme } from "@mui/material/styles";
import { HRService } from "../../../config";
import { executeAnyMutation } from "../../../data/DynamicServiceNew/jsondynamicservice";

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

export const StaffClaimApprovalDialog = ({ open, onClose, Row }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [comments, setComments] = useState("");

  const handleAction = async (actionType) => {
    try {
      onClose(); // Close early for UX

      const confirmation = await Swal.fire({
        title: `Are you sure you want to ${actionType}?`,
        icon: actionType === "approve" ? "success" : "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, ${actionType}`,
        cancelButtonText: "Cancel",
      });

      if (!confirmation.isConfirmed) return;

      const mutationData = {
        applicationId: Row?.Id,
        comments: comments || null,
        action: actionType,
      };

      const response = await executeAnyMutation(
        "approveDeclineStaffClaimApplication",
        mutationData,
        "applicationsApprovals",
        false,
        HRService.uri 
      );

      if (response.results) {
        Swal.fire("Success", `Claim ${actionType}d successfully.`, "success");
      } else {
        throw new Error(response.error || `${actionType} failed`);
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Something went wrong", "error");
    }
  };

  const handleClose = () => {
    setComments("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <IconButton
        sx={{ position: "absolute", right: 8, top: 8, color: theme.palette.grey[500] }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>

      <Box sx={{ bgcolor: "background.paper", p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Approve / Decline Claim
          <Divider sx={{ borderBottomWidth: 2, border: "1px solid #003049", my: 1 }} />
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <StyledCard variant="outlined">
              <Typography>
                Claimant: <strong>{Row?.ApplicantFirstName} {Row?.ApplicantLastName}</strong>
              </Typography>
              <Typography>
                Claim Date: <strong>{dayjs(Row?.CreatedDate).format("MMM DD, YYYY")}</strong>
              </Typography>
              <Typography>
                Claim Title: <strong>{Row?.Title}</strong>
              </Typography>
              <Typography>
                Amount: <strong>{Row?.TotalAmount?.toFixed(2)}</strong>
              </Typography>
            </StyledCard>

            <TextField
              label="Comments (Optional)"
              multiline
              rows={4}
              fullWidth
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              sx={{ mt: 2 }}
            />

            <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                color="success"
                fullWidth
                onClick={() => handleAction("approve")}
              >
                Approve and Send
              </Button>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={() => handleAction("decline")}
              >
                Decline
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};
